/**
 * NoteEssayLinker — links and unlinks essays to/from a note.
 *
 * Displays linked essays as closable tags (click to navigate, X to unlink)
 * and provides a modal picker to search and add new essay links.
 *
 * Used by:
 * - src/pages/NoteEditorPage.tsx
 */
import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Tag, Button, Modal, Input, List, Badge, message, Spin } from 'antd';
import { LinkOutlined, CheckOutlined, SearchOutlined } from '@ant-design/icons';
import { getEssays, addNoteLink, removeNoteLink } from '../services/api';
import type { EssayListItem } from '@compendium/shared';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface NoteEssayLinkerProps {
  noteId: number;
  linkedEssays: Array<{ id: number; slug: string; title: string }>;
}

// ---------------------------------------------------------------------------
// Status badge color map
// ---------------------------------------------------------------------------

const statusColors: Record<string, string> = {
  published: 'green',
  draft: 'default',
  review: 'default',
  archived: 'red',
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function NoteEssayLinker({ noteId, linkedEssays }: NoteEssayLinkerProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [modalOpen, setModalOpen] = useState(false);
  const [search, setSearch] = useState('');

  // ---- Fetch all essays only when modal is open ----
  const { data: allEssays = [], isLoading: essaysLoading } = useQuery<EssayListItem[]>({
    queryKey: ['essays'],
    queryFn: () => getEssays(),
    enabled: modalOpen,
  });

  // ---- Mutations ----
  const linkMutation = useMutation({
    mutationFn: (essayId: number) => addNoteLink(noteId, essayId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['note', noteId] });
      message.success('Essay linked');
    },
    onError: () => {
      message.error('Failed to link essay');
    },
  });

  const unlinkMutation = useMutation({
    mutationFn: (essayId: number) => removeNoteLink(noteId, essayId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['note', noteId] });
      message.success('Essay unlinked');
    },
    onError: () => {
      message.error('Failed to unlink essay');
    },
  });

  // ---- Derived data ----
  const linkedIds = useMemo(() => new Set(linkedEssays.map((e) => e.id)), [linkedEssays]);

  const filteredEssays = useMemo(() => {
    const term = search.toLowerCase().trim();
    if (!term) return allEssays;
    return allEssays.filter((e) => e.title.toLowerCase().includes(term));
  }, [allEssays, search]);

  // ---- Handlers ----
  function handleTagClick(slug: string) {
    navigate(`/reviewer/essays/${slug}`);
  }

  function handleUnlink(essayId: number) {
    unlinkMutation.mutate(essayId);
  }

  function handleLink(essayId: number) {
    linkMutation.mutate(essayId);
  }

  function openModal() {
    setSearch('');
    setModalOpen(true);
  }

  // ---- Render ----
  return (
    <div style={{ borderTop: '1px solid #222', paddingTop: 16, marginTop: 24 }}>
      {/* Section label */}
      <div
        style={{
          fontSize: 11,
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          color: '#888',
          marginBottom: 10,
        }}
      >
        Linked Essays
      </div>

      {/* Linked essay tags */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
        {linkedEssays.length === 0 && (
          <span style={{ color: '#666', fontSize: 13 }}>No linked essays</span>
        )}

        {linkedEssays.map((essay) => (
          <Tag
            key={essay.id}
            closable
            onClose={(e) => {
              e.preventDefault();
              handleUnlink(essay.id);
            }}
            style={{
              cursor: 'pointer',
              background: 'transparent',
              border: '1px solid #222222',
              color: '#d4af37',
              borderRadius: 4,
            }}
            onClick={() => handleTagClick(essay.slug)}
          >
            {essay.title}
          </Tag>
        ))}

        {/* Link essay button */}
        <Button
          type="dashed"
          size="small"
          icon={<LinkOutlined />}
          onClick={openModal}
          style={{ color: '#888', borderColor: '#333' }}
        >
          Link Essay
        </Button>
      </div>

      {/* Essay picker modal */}
      <Modal
        title="Link to Essay"
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        footer={null}
        destroyOnHidden
      >
        {/* Search input */}
        <Input
          placeholder="Filter essays..."
          prefix={<SearchOutlined style={{ color: '#666' }} />}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          allowClear
          style={{ marginBottom: 12 }}
        />

        {/* Essay list */}
        {essaysLoading ? (
          <div style={{ textAlign: 'center', padding: 32 }}>
            <Spin />
          </div>
        ) : (
          <List
            dataSource={filteredEssays}
            locale={{ emptyText: 'No essays found' }}
            style={{ maxHeight: 400, overflowY: 'auto' }}
            renderItem={(essay: EssayListItem) => {
              const alreadyLinked = linkedIds.has(essay.id);
              return (
                <List.Item
                  style={{
                    cursor: alreadyLinked ? 'default' : 'pointer',
                    opacity: alreadyLinked ? 0.5 : 1,
                    padding: '10px 12px',
                  }}
                  onClick={() => {
                    if (!alreadyLinked) handleLink(essay.id);
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      width: '100%',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      {alreadyLinked && <CheckOutlined style={{ color: '#52c41a', fontSize: 12 }} />}
                      <span>{essay.title}</span>
                    </div>
                    <Badge
                      color={statusColors[essay.status] ?? 'default'}
                      text={
                        <span style={{ fontSize: 12, color: '#999' }}>{essay.status}</span>
                      }
                    />
                  </div>
                </List.Item>
              );
            }}
          />
        )}
      </Modal>
    </div>
  );
}
